// START IMPORTS
// Context imports
import {
  AppConfig,
  AppConstants,
  AppRequests,
  createAppConfig,
} from '@ltvco/refresh-lib/ctx';
// Theme imports
import { styled } from '@ltvco/refresh-lib/theme';
// Core lib v1 imports
import { AlertProvider } from '@ltvco/refresh-lib/v1';
// Vendor imports
import { ExitToApp } from '@mui/icons-material';
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Link,
  useParams,
} from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { appRequests } from 'appRequests';

// Type imports
import type { ResponseError } from '@ltvco/refresh-lib/utils';
import type { SideMenuItem } from '@ltvco/refresh-lib/v2';
// END IMPORTS

// START STYLED_COMPONENTS
const MenuItem = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.fontSize * 1.125,
  display: 'flex',
  alignItems: 'center',
  svg: {
    marginRight: theme.spacing(1),
  },
}));
// END STYLED_COMPONENTS

// START HELPER_FUNCTIONS
export function LibContextWrapper({
  children,
  trackEventGA4,
  trackEvent,
  logError,
  handleLogoutClick,
}: {
  children: React.ReactNode | React.ReactNode[];
  trackEventGA4: (eventToTrack: object, eventName?: string) => void;
  trackEvent: (category: string, action: string, label?: string) => void;
  logError: (
    context: string,
    error: Error | ResponseError,
    url: string | undefined
  ) => void;
  handleLogoutClick: () => void;
}) {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const navigate = useNavigate();
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const defaultConfig = createAppConfig();
  const { sideMenuItems } = constants.config;
  // END DERIVED_DATA_AND_UTILS

  function flattenMenuItems(menuItems: SideMenuItem[]): SideMenuItem[] {
    return menuItems.flatMap(({ childItems, ...item }) => [
      item,
      ...(childItems?.length ? flattenMenuItems(childItems) : []),
    ]);
  }

  const flatMenu = flattenMenuItems(constants.config.sideMenuItems);
  const menuItems = flatMenu.map((item) => (
    <MenuItem to={item.route} data-cy={`menu_${item['data-cy']}`}>
      {item.icon} {item.text}
    </MenuItem>
  ));

  function trackListhub(eventName: string, listingKey: string) {
    const listhubFn = (window as any).lh;
    if (listhubFn && typeof listhubFn !== 'undefined') {
      if (eventName && listingKey) {
        listhubFn('submit', eventName || null, { lkey: listingKey || null });
      }
    }
  }

  const appConfig = {
    ...defaultConfig,
    routingUtils: {
      ...defaultConfig.routingUtils,
      navigate: navigate,
      location: location,
      useSearchParams,
      useParams,
    },
    logError: logError,
    trackEvent: trackEvent,
    trackEventGA4: trackEventGA4,
    trackListhub: trackListhub,
  };

  const appConstants = {
    ...constants,
    config: {
      ...constants.config,
      menuItems: [
        ...menuItems,
        <MenuItem
          to="#"
          onClick={() => handleLogoutClick()}
          data-cy="menu_log_out_btn"
        >
          <ExitToApp sx={{ color: '#24537B' }} /> Log Out
        </MenuItem>,
      ],
      sideMenuItems: [
        ...sideMenuItems,
        {
          route: '',
          icon: '',
          text: 'Logout',
          name: 'logout',
          state: '',
          'data-cy': 'menu_log_out_btn',
          action: handleLogoutClick,
        },
      ],
    },
  };

  // START RENDER
  return (
    <AlertProvider maxSnack={3} preventDuplicate autoHideDuration={5000}>
      <AppRequests.Provider value={appRequests}>
        <AppConfig.Provider value={appConfig as any}>
          <AppConstants.Provider value={appConstants}>
            {children}
          </AppConstants.Provider>
        </AppConfig.Provider>
      </AppRequests.Provider>
    </AlertProvider>
  );
  // END RENDER
}
// END HELPER_FUNCTIONS
